import { useState } from "react";
import { CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface TriageItem {
  id: string;
  description: string;
}

interface FunctionalTriageProps {
  items: TriageItem[];
  productName: string;
  onSkipToErrorCodes?: () => void;
  onAnalyzeResults?: () => void;
}

type ItemStatus = "working" | "not-working" | null;

export function FunctionalTriage({
  items,
  productName,
  onSkipToErrorCodes,
  onAnalyzeResults,
}: FunctionalTriageProps) {
  const { t } = useTranslation('repair');
  const [statuses, setStatuses] = useState<Record<string, ItemStatus>>({});

  const completedCount = Object.values(statuses).filter((s) => s !== null).length;
  const progressPercent = (completedCount / items.length) * 100;
  const allCompleted = completedCount === items.length;

  const handleStatusChange = (itemId: string, status: ItemStatus) => {
    setStatuses((prev) => ({
      ...prev,
      [itemId]: prev[itemId] === status ? null : status,
    }));
  };

  const handleAnalyze = () => {
    const notWorkingItems = Object.entries(statuses)
      .filter(([_, status]) => status === "not-working")
      .map(([id]) => items.find((item) => item.id === id)?.description);

    if (notWorkingItems.length > 0) {
      toast.info(t('triage.analyzing', { count: notWorkingItems.length }));
    } else {
      toast.success(t('triage.allWorking'));
    }
    onAnalyzeResults?.();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-foreground">{t('triage.title')}</h4>
        <span className="text-sm text-muted-foreground">
          {t('triage.completed', { done: completedCount, total: items.length })}
        </span>
      </div>

      <Progress value={progressPercent} className="h-2 mb-6" />

      <div className="space-y-3">
        {items.map((item) => {
          const status = statuses[item.id];
          return (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 bg-background rounded-xl border border-border"
            >
              <span className="text-foreground">{item.description}</span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleStatusChange(item.id, "working")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    status === "working"
                      ? "bg-refurbish text-refurbish-foreground border-refurbish"
                      : "border-refurbish/50 text-refurbish hover:bg-refurbish/5"
                  }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('triage.working')}
                </button>
                <button
                  onClick={() => handleStatusChange(item.id, "not-working")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    status === "not-working"
                      ? "bg-destructive text-destructive-foreground border-destructive"
                      : "border-destructive/50 text-destructive hover:bg-destructive/5"
                  }`}
                >
                  <XCircle className="w-4 h-4" />
                  {t('triage.notWorking')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <Button variant="outline" onClick={onSkipToErrorCodes}>
          {t('triage.skip')}
        </Button>
        <Button
          onClick={handleAnalyze}
          disabled={!allCompleted}
          className="bg-primary hover:bg-primary/90"
        >
          {t('triage.analyze')}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
